import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { GpAuthService, isAuthRejected, ResetAuthState } from 'gp-auth';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

const REFRESH_URL = '/api/auth/refresh-token/';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private AUTH_HEADER = 'Authorization';

  constructor(private auth: GpAuthService, private store: Store) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Сам refresh-запит не оновлює токен і не розлогінює — це вирішує той, хто його викликав
    if (req.url.endsWith(REFRESH_URL)) {
      return next.handle(req);
    }

    if (this.shouldRefreshToken()) {
      return this.refreshThen(() => next.handle(this.addAuthenticationToken(req)));
    }

    return next.handle(this.addAuthenticationToken(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }
        if (!this.auth.getToken()) {
          this.resetAuth();
          return throwError(() => error);
        }
        return this.refreshThen(() => next.handle(this.addAuthenticationToken(req)));
      })
    );
  }

  /** Оновлює токен (один запит на всіх) і повторює запит. Розлогінює лише якщо сервер відхилив сесію. */
  private refreshThen(retry: () => Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return this.auth.refreshToken().pipe(
      catchError((err) => {
        if (isAuthRejected(err)) {
          this.resetAuth();
        }
        return throwError(() => err);
      }),
      switchMap(() => retry())
    );
  }

  private shouldRefreshToken(): boolean {
    const token = this.auth.getToken();
    if (!token) return false;
    try {
      const exp = (jwtDecode(token) as any).exp as number;
      if (!exp) return false;
      return (exp - 200) < DateTime.now().toUnixInteger();
    } catch {
      return false;
    }
  }

  private resetAuth(): void {
    this.store.dispatch(new ResetAuthState());
    this.store.dispatch(new Navigate(['/auth/login']));
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.auth.getToken();
    if (!token) {
      return request;
    }

    const headers = request.headers
      .set(this.AUTH_HEADER, `Bearer ${token}`)
      .set('Time-Zone', Intl.DateTimeFormat().resolvedOptions().timeZone);

    return request.clone({ headers });
  }
}
